import { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { InputSwitch } from "primereact/inputswitch";
import { Fieldset } from "primereact/fieldset";
import { InputNumber } from "primereact/inputnumber";

import { RoutingRule } from "./RoutingRule";
import FormGroup from "../../common/components/form/FormGroup";
import TeamsDropdown from "../teams/TeamsDropdown";
import TicketTypeDropdown from "../ticketType/TicketTypeDropdown";
import DepartmentsDropdown from "../departments/DepartmentsDropdown";
import LocationsMultiSelect from "../locations/LocationsMultiSelect";
import CategoriesDropdown from "../categories/CategoriesDropdown";

interface RoutingRuleFormProps {
  routingRule?: RoutingRule;
  onSubmit: (routingRule: RoutingRule) => Promise<void>;
}

export default function RoutingRuleForm(props: RoutingRuleFormProps) {
  const {
    register,
    control,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm<RoutingRule>({ defaultValues: props.routingRule ? props.routingRule : { executionOrder: 1, locations: [] } });
  const [isLoading, setIsLoading] = useState(false);
  const watchedDepartmentId = watch("departmentId");
  const watchedTicketType = watch("ticketType");

  useEffect(() => {
    if (props.routingRule && props.routingRule.departmentId !== watchedDepartmentId) {
      setValue("categories", []);
      setValue("teamId", "");
    }
  }, [watchedDepartmentId]);

  const onSubmit = async (data: RoutingRule) => {
    setIsLoading(true);
    await props.onSubmit(data);
    setIsLoading(false);
  };

  return (
    <form className="flex flex-column gap-2" onSubmit={handleSubmit(onSubmit)}>
      <Fieldset legend="Conditii">
        <FormGroup inline label="Activa">
          <Controller
            name="isActive"
            control={control}
            render={({ field }) => <InputSwitch checked={field.value} onChange={(e) => field.onChange(e.value)} />}
          ></Controller>
        </FormGroup>
        <div className="flex flex-column md:flex-row gap-2">
          <FormGroup className="w-full md:w-4" label="Nume *" errorMessage={errors.name?.message}>
            <InputText
              {...register("name", {
                required: "Numele este obligatoriu",
                validate: (value) => value.trim() !== "" || "Numele este obligatoriu",
              })}
            />
          </FormGroup>
          <FormGroup className="w-full md:w-4" label="Ordine executie">
            <Controller
              name="executionOrder"
              control={control}
              render={({ field }) => (
                <InputNumber
                  className="w-full"
                  inputClassName="w-full"
                  disabled
                  value={field.value}
                  onChange={(e) => field.onChange(e.value)}
                ></InputNumber>
              )}
            />
          </FormGroup>
          <FormGroup className="w-full md:w-4" label="Departament *" errorMessage={errors.departmentId?.message}>
            <Controller
              name="departmentId"
              control={control}
              rules={{ required: "Departamentul este obligatoriu" }}
              render={({ field }) => (
                <DepartmentsDropdown optionsType="withoutRequiredAgent" value={field.value} onChange={field.onChange} />
              )}
            />
          </FormGroup>
        </div>
        <FormGroup label="Locatii">
          <Controller
            name="locations"
            control={control}
            render={({ field }) => (
              <LocationsMultiSelect
                value={field.value?.map((loc) => loc.id)}
                onChange={(ids: string[]) => {
                  field.onChange(ids.map((id) => ({ id })));
                }}
              />
            )}
          />
        </FormGroup>
        <FormGroup label="Tip tichet">
          <Controller
            name="ticketType"
            control={control}
            render={({ field }) => (
              <TicketTypeDropdown placeholder="Selecteaza tip tichet" showClear value={field.value} onChange={field.onChange} />
            )}
          ></Controller>
        </FormGroup>
        {watchedDepartmentId != null && (
          <FormGroup label="Categorie">
            <Controller
              name="categories"
              control={control}
              render={({ field }) => (
                <CategoriesDropdown
                  key={watchedDepartmentId}
                  column
                  departmentId={watchedDepartmentId}
                  ticketType={watchedTicketType}
                  firstLevelValue={field.value?.[0]?.id}
                  secondLevelValue={field.value?.[1]?.id}
                  thirdLevelValue={field.value?.[2]?.id}
                  onChangeAllLevels={field.onChange}
                />
              )}
            ></Controller>
          </FormGroup>
        )}
      </Fieldset>
      <Fieldset legend="Actiune">
        <FormGroup label="Atribuie grupa *" errorMessage={errors.teamId?.message}>
          <Controller
            name="teamId"
            control={control}
            rules={{ required: "Grupa este obligatorie" }}
            render={({ field }) => <TeamsDropdown departmentId={watchedDepartmentId} value={field.value} onChange={field.onChange} />}
          />
        </FormGroup>
      </Fieldset>
      <Button type="submit" className="block ml-auto" label="Salveaza" icon="pi pi-save" loading={isLoading} disabled={isLoading}></Button>
    </form>
  );
}
